import { Injectable }  from '@angular/core';
import { PtyWsService } from './services/pty-ws.service'
import { Device } from '@capacitor/device';
import { PushNotifications } from '@capacitor/push-notifications';
// Doesn't work with import for some reason
const moment = require('moment/min/moment-with-locales');
import setLocalStorageValue from './utils/local-storage';
import { TranslationService } from "./services/translation.service";
import { AuthService } from "./services/auth.service";
import { DeepLinkService } from "./services/deep-link.service";
import { ActivatedRoute } from "@angular/router";


@Injectable()
export class AppInitService {
    translationObject: any = { translation: null, countries: null};
    apiVersion: number = 0;
    defaultLocale: string = 'en';

    constructor(
        private ws: PtyWsService,
        private _translationService: TranslationService,
        private auth: AuthService,
        private deepLinkService: DeepLinkService,
        private route: ActivatedRoute,
    ) {
    }

    /* checks of local storage data exists for user */
    isAuthenticated() {
        return this.auth.isAuthenticatedFromLocalStorage();
    }

    /* parses local storage data for user and returns user object */
    localUser() {
        const userStorage = this.auth.getParsedSessionData();
        return userStorage.user;
    }

    /* returns login page data from the api - translation, countries and api_version */
    initLoginPage(){
        let self = this;
        let locale = localStorage.getItem('app_locale');
        if (location.search) {
            const queryParams = new URLSearchParams(location.search);
            const queryLang = queryParams.get('lang');
            if( queryLang ) {
                locale = queryLang;
            }
        }

        return new Promise((resolve, reject) => {
            self.ws.invokeURL('init_login_page', {locale}).subscribe((res: any) => {
                if (res.error === 0) {
                    const defaultTranslation = require('../assets/i18n/en.json');
                    self.translationObject.translation = JSON.stringify({...defaultTranslation, ...res.result.translation});
                    self.translationObject.countries = JSON.stringify(res.result.countries);
                    self.apiVersion = res.result.api_version;

                    localStorage.setItem('app_translation', self.translationObject.translation);
                    localStorage.setItem('country_translation', self.translationObject.countries);
                }
                resolve(true);
            }, (err: any) => {
                console.error('Translation not loaded', err);
                resolve(true);
            });
        });
    }

    /* uses data from url to return locale, otherwise returns default locale */
    getLocaleFromUrl(){
        let locale = this.defaultLocale;
        const urlParams = new URLSearchParams(window.location.search);
        const languageCode = urlParams.get('lang');
        //
        if (languageCode) return languageCode.substr(0, 2).toLowerCase();
        if (navigator) {
            locale = navigator.language;
            return locale.substr(0, 2).toLowerCase();
        }
        return locale;
    }

    /* uses data from local storage user object to return locale */
    getLocaleFromUser() {
        let user = this.localUser();
        return user && user.code_1 ? user.code_1 : this.defaultLocale;
    }

    // set local storage value for app locale
    async setAppLocale(){
        // console.log('setAppLocale');

        let locale = this.defaultLocale; // use english as our default locale
        const info = await Device.getInfo();
        console.log('Device info: ', info);
        if(this.isAuthenticated()) {
            locale = this.getLocaleFromUser();
        } else if (info.platform === 'ios' || info.platform === 'android') {
            const languageData = await Device.getLanguageCode();
            console.log('Language data: ', languageData.value);
            locale = languageData.value.toLowerCase();
        } else {
            console.log('Getting locale from url');
            locale = this.getLocaleFromUrl();
        }
        /* set locale for moment based on returned locale - comment out for now, it currently just
         * appears to change display dates to country language, which breaks api calls that include them (esp. arabic) */
        // moment.locale(locale);
        console.log('new locale: ', locale);
        setLocalStorageValue('app_locale', locale);
        return locale;
    }

    setAppLanguageId(appLocale) {
        if (appLocale == null) {
            return;
        }

        this.ws.invokeURL('languages', {}).subscribe(function (result) {
            let languages = result['languages'];
            const appLanguage = languages.find((language) => language.code_1 == appLocale);
            // console.log('appLanguage', appLanguage);

            if (appLanguage) {
                const appLanguageId = appLanguage.id;
                // console.log('appLanguageId', appLanguageId);
                setLocalStorageValue('app_language_id', appLanguageId);
            }

        }, function (err) {
            console.log('Error loading languages', err);
        });
    }

    // set local storage values for translation and countries
    setTranslation() {
        setLocalStorageValue('app_translation', this.translationObject.translation);
        setLocalStorageValue('country_translation', this.translationObject.countries);
        this._translationService.triggerRefresh();
    }

    // set local storage value for api version
    setApiVersion(){
        setLocalStorageValue('api_version', this.apiVersion)
    }

    Init() {
        let self = this;
        return new Promise(async (resolve, reject) => {
            /* set app locale as a local storage value */
            let appLocale = await this.setAppLocale();
            this.setAppLanguageId(appLocale);

            /* initialize default local storage values */
            setLocalStorageValue('SuggestedResponseCRM', 'true');
            setLocalStorageValue('tooltip', 'true');
            setLocalStorageValue('MapDisplayStatus', 'true');
            setLocalStorageValue('suggestedResponseStatus', 'true');
            setLocalStorageValue('NotificationToggleStatus', 'true');

            let user = this.localUser();

            const info = await Device.getInfo();

            // Only initialize push notifications for native app users
            if (info.platform === 'ios' || info.platform === 'android') {
                await this.addPushNotificationListeners(info.platform);
            }
            if (info.platform === 'web') {
                const currentUrl = document.location.pathname + document.location.search;
                this.deepLinkService.queueRequestedUrlToHandleAfterLogin(currentUrl);
            }

            // call login-page related tasks if not authenticated:
            if(!this.isAuthenticated() || !user){

              // return translation, countries and api version from the api based on app_locale
               this.initLoginPage().then((result)=>{

                    // set local storage values for translation and countries
                    self.setTranslation();

                    // set local storage value for the api version
                    self.setApiVersion();

                    // resolve and continue loading the application from app.component
                    resolve(true);
                })
           } else{
                // if authenticated

                // get our current user from local storage
                let user = this.localUser();

                // change SuggestedResponseCRM to the user's "switch"
                // this was a holdover from app.component but has been left here so we don't unintentionally mess anything up
                // it's called from the 'seeker-request' page
                // TODO: research this further to see how necessary it is,  and if there's a better way to handle
                localStorage.setItem('SuggestedResponseCRM', user.switch);

                // set local storage values for translation and countries based on the local storage data from the user
                self.translationObject.translation = JSON.stringify(user.translation);
                self.translationObject.countries = JSON.stringify(user.countries);
                self.setTranslation();

                self.auth.initPushNotifications();

                // resolve and continue loading the application from app.component
                resolve(true);
            }
        });
    }

    async addPushNotificationListeners(platform: string) {
        await PushNotifications.addListener('registration', token => {
            if (!token || !token.value) {
                return;
            }

            console.log('Attempting to register push notifications');

            // Not using this.localUser() because I need the token
            const ptyUserData = localStorage.getItem('pty_user_data');
            if (!ptyUserData || ptyUserData === '') {
                return;
            }

            let userStorage: any = {};
            if (ptyUserData) {
                userStorage = JSON.parse(ptyUserData);
            }

            if (!userStorage.user) {
                return;
            }

            if (platform === userStorage.user.push_notifications_platform
                && token.value === userStorage.user.push_notifications_token
            ) {
                return;
            }

            // Make an API call to register push notifications
            this.ws.invokeURL('set_user_token_notifications', {
                email: userStorage.user.email,
                token: userStorage.token,
                username: userStorage.user.username,
                uuid: userStorage.user.uuid,

                platform: platform,
                pushNotificationToken: token.value,
            }).subscribe((result: any) => {
                // Ignoring for now
                console.log('Registered push notification token', platform, token);
            }, (err: any) => {
                // Ignoring for now
                console.log('Error registering push notification token', platform, token);
            });
        });

        await PushNotifications.addListener('registrationError', err => {
            console.error('Registration error: ', err.error);
        });

        await PushNotifications.addListener('pushNotificationReceived', notification => {
            console.log('Push notification received: ', notification);
        });

        await PushNotifications.addListener('pushNotificationActionPerformed', notification => {
            if (this.isAuthenticated()) {
                this.deepLinkService.handleDeepLinkFromPushNotification(notification);
                console.log('Push notification action performed', notification.actionId, notification.inputValue);
            } else {
                this.deepLinkService.queueDeepLinkClickToHandleAfterLogin(notification);
            }
        });
    }

}
